/* PublicObservatorio — Indicadores agregados + ranking de países + distribuição por vetor. */
import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, Globe2, TrendingUp, TrendingDown, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const fmtNum = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);

export default function PublicObservatorio() {
  const [countries, setCountries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/countries`),
      axios.get(`${API}/areas`),
      axios.get(`${API}/overview`),
    ]).then(([c, a, o]) => {
      setCountries(c.data || []);
      setAreas(a.data || []);
      setOverview(o.data);
    }).catch(() => {});
  }, []);

  const totalArea = areas.reduce((a, b) => a + (b.value || 0), 0) || 1;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 space-y-10" data-testid="public-observatorio-page">
      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">Painel Público</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0a2342] mt-1">Observatório de Produção Prospectiva</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Indicadores anônimos sobre publicações científicas, estratégicas e institucionais monitoradas em
          relação aos 9 vetores oficiais do Projeto Nostradamus.
        </p>
      </div>

      {/* KPIs */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Documentos Totais" value={fmtNum(overview.total_documents)} icon={BarChart3} accent />
          <Kpi label="Países Acompanhados" value={fmtNum(overview.total_countries)} icon={Globe2} />
          <Kpi label="Novos · últimos 7d" value={fmtNum(overview.total_last_7_days)} icon={TrendingUp} />
          <Kpi label="Vetor Dominante" value={overview.dominant_area_label || "—"} icon={BarChart3} small />
        </div>
      )}

      {/* Distribuição por Vetor */}
      <section data-testid="dist-vetor">
        <h2 className="font-display font-bold text-xl text-[#0a2342] mb-4">Distribuição por Vetor Temático</h2>
        <div className="bg-white border border-slate-200 rounded p-5 space-y-3">
          {areas.map((a) => {
            const pct = ((a.value || 0) / totalArea) * 100;
            return (
              <div key={a.code} data-testid={`area-row-${a.code}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-semibold text-[#0a2342]">{a.label}</span>
                  <span className="text-xs font-mono text-slate-500">{a.value} · {pct.toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#0a2342] to-amber-400" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {areas.length === 0 && <div className="text-sm text-slate-500 text-center py-4">Sem dados agregados ainda.</div>}
        </div>
      </section>

      {/* Ranking por país */}
      <section data-testid="ranking-paises">
        <h2 className="font-display font-bold text-xl text-[#0a2342] mb-4">Ranking de Países por Produção Monitorada</h2>
        <div className="bg-white border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">País</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Últ. 7d</th>
                <th className="px-4 py-3 text-right">Δ Semanal</th>
                <th className="px-4 py-3 text-left">Vetor Dominante</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c, i) => (
                <tr key={c.country_code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`country-row-${c.country_code}`}>
                  <td className="px-4 py-3 text-slate-400 font-mono">{i + 1}</td>
                  <td className="px-4 py-3 font-semibold text-[#0a2342]">{c.country} <span className="text-[10px] font-mono text-slate-400 ml-1">{c.country_code}</span></td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{c.total_documents}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.documents_last_7_days}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    <span className="inline-flex items-center gap-1">
                      {c.weekly_variation > 0 ? <TrendingUp size={11} className="text-emerald-600" /> :
                       c.weekly_variation < 0 ? <TrendingDown size={11} className="text-rose-600" /> :
                       <Minus size={11} className="text-slate-400" />}
                      {c.weekly_variation > 0 ? "+" : ""}{c.weekly_variation}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{c.dominant_area_label || c.dominant_area}</td>
                </tr>
              ))}
              {countries.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400 text-sm">Aguardando primeira coleta…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, small }) {
  return (
    <div className={`p-5 rounded border ${accent ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
          <div className={`font-display font-bold text-[#0a2342] mt-2 ${small ? "text-lg" : "text-3xl"}`}>{value}</div>
        </div>
        {Icon && <Icon size={20} className="text-amber-500" strokeWidth={1.5} />}
      </div>
    </div>
  );
}
